import { cn } from '@crm/ui';
import { UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';

/** SHA-256 em hexadecimal - e o que o Gravatar aceita hoje. */
async function sha256(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', bytes);

  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

interface AvatarProps {
  name: string;
  /** Foto do cadastro; e sempre ela quando existe. */
  image?: string | null;
  /** Sem foto, o e-mail vira um avatar sempre igual para a mesma pessoa. */
  email?: string | null;
  className?: string;
}

/**
 * Cara do cliente em tres degraus: a foto do cadastro, o avatar do e-mail
 * (Gravatar, que cai num desenho unico por endereco) e, sem nada disso, as
 * iniciais - que nunca falham e nao pedem rede.
 */
export function Avatar({ name, image, email, className }: AvatarProps) {
  const [fromEmail, setFromEmail] = useState<string | null>(null);
  /**
   * Guarda o endereco que falhou, nao um "falhou" solto: com o booleano, uma
   * foto quebrada uma vez deixava as iniciais no lugar para sempre - trocar a
   * foto salvava no servidor e a tela nao mudava.
   */
  const [quebrada, setQuebrada] = useState<string | null>(null);

  useEffect(() => {
    if (image || !email) {
      setFromEmail(null);
      return;
    }

    let active = true;
    sha256(email.trim().toLowerCase()).then((hash) => {
      if (active) setFromEmail(`https://www.gravatar.com/avatar/${hash}?s=96&d=identicon`);
    });

    return () => {
      active = false;
    };
  }, [image, email]);

  const src = image || fromEmail;

  if (src && src !== quebrada) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => setQuebrada(src)}
        className={cn(
          'h-10 w-10 shrink-0 rounded-full border border-border object-cover',
          className,
        )}
      />
    );
  }

  // Sem foto, um icone de pessoa - o mesmo para todo mundo. As iniciais
  // pediam leitura para dizer o que o nome ao lado ja diz.
  return (
    <span
      title={name}
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary',
        className,
      )}
    >
      <UserRound className="h-[55%] w-[55%]" aria-hidden />
    </span>
  );
}
